import { Edit2, ExternalLink, Image, Palette, Plus, Ruler, Trash2, Type, icons } from "lucide-react";
import { useMemo, useState } from "react";

// Custom variables icon - hexagon with centered circle
const VariablesIcon = ({ size = 16, className = "" }: { size?: number; className?: string }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <title>Variables Icon</title>
    {/* Hexagon outline */}
    <polygon
      points="12,2 20,6 20,18 12,22 4,18 4,6"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    />
    {/* Centered circle */}
    <circle cx="12" cy="12" r="4" fill="currentColor" />
  </svg>
);

import { HexColorPicker } from "react-colorful";
import { Link, useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useEditorStore } from "@/store/editorStore";
import {
  type ColorVariable,
  type FontFamilyVariable,
  type IconVariable,
  type SizeVariable,
  useVariablesStore,
} from "@/store/variablesStore";

const Variables = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const { project } = useEditorStore();
  const {
    getProjectVariables,
    getCraftspaceVariables,
    addColorVariable,
    updateColorVariable,
    deleteColorVariable,
    addSizeVariable,
    updateSizeVariable,
    deleteSizeVariable,
    addFontFamilyVariable,
    updateFontFamilyVariable,
    deleteFontFamilyVariable,
    addIconVariable,
    updateIconVariable,
    deleteIconVariable,
    addCraftspaceColorVariable,
    updateCraftspaceColorVariable,
    deleteCraftspaceColorVariable,
    addCraftspaceSizeVariable,
    updateCraftspaceSizeVariable,
    deleteCraftspaceSizeVariable,
    addCraftspaceFontFamilyVariable,
    updateCraftspaceFontFamilyVariable,
    deleteCraftspaceFontFamilyVariable,
    addCraftspaceIconVariable,
    updateCraftspaceIconVariable,
    deleteCraftspaceIconVariable,
  } = useVariablesStore();

  const projectVariables = projectId
    ? getProjectVariables(projectId)
    : { colors: [], sizes: [], fontFamilies: [], icons: [] };
  const craftspaceVariables = project?.craftspace_id
    ? getCraftspaceVariables(project.craftspace_id)
    : { colors: [], sizes: [], fontFamilies: [], icons: [] };
  const hasCraftspace = Boolean(project?.craftspace_id);
  const craftspaceId = project?.craftspace_id;


  const craftspaceColors = craftspaceVariables.colors || [];
  const projectColors = projectVariables.colors || [];
  const craftspaceSizes = craftspaceVariables.sizes || [];
  const projectSizes = projectVariables.sizes || [];
  const craftspaceFonts = craftspaceVariables.fontFamilies || [];
  const projectFonts = projectVariables.fontFamilies || [];
  const craftspaceIcons = craftspaceVariables.icons || [];
  const projectIcons = projectVariables.icons || [];

  // Dialog states
  const [colorDialogOpen, setColorDialogOpen] = useState(false);
  const [sizeDialogOpen, setSizeDialogOpen] = useState(false);
  const [fontDialogOpen, setFontDialogOpen] = useState(false);
  const [iconDialogOpen, setIconDialogOpen] = useState(false);

  // Form states
  const [newColorName, setNewColorName] = useState("");
  const [newColorValue, setNewColorValue] = useState("#3B82F6");
  const [editingColor, setEditingColor] = useState<ColorVariable | null>(null);
  const [colorScope, setColorScope] = useState<"project" | "craftspace">("project");

  const [newSizeName, setNewSizeName] = useState("");
  const [newSizeValue, setNewSizeValue] = useState("");
  const [newSizeUnit, setNewSizeUnit] = useState("px");
  const [editingSize, setEditingSize] = useState<SizeVariable | null>(null);
  const [sizeScope, setSizeScope] = useState<"project" | "craftspace">("project");

  const [newFontName, setNewFontName] = useState("");
  const [newFontValue, setNewFontValue] = useState("");
  const [editingFont, setEditingFont] = useState<FontFamilyVariable | null>(null);
  const [fontScope, setFontScope] = useState<"project" | "craftspace">("project");

  const [newIconName, setNewIconName] = useState("");
  const [newIconValue, setNewIconValue] = useState("");
  const [iconNameTouched, setIconNameTouched] = useState(false);
  const [editingIcon, setEditingIcon] = useState<IconVariable | null>(null);
  const [iconScope, setIconScope] = useState<"project" | "craftspace">("project");
  const [iconSearch, setIconSearch] = useState("");

  const resetColorForm = () => {
    setNewColorName("");
    setNewColorValue("#3B82F6");
    setEditingColor(null);
    setColorScope("project");
  };

  const resetSizeForm = () => {
    setNewSizeName("");
    setNewSizeValue("");
    setNewSizeUnit("px");
    setEditingSize(null);
    setSizeScope("project");
  };

  const resetFontForm = () => {
    setNewFontName("");
    setNewFontValue("");
    setEditingFont(null);
    setFontScope("project");
  };

  const resetIconForm = () => {
    setNewIconName("");
    setNewIconValue("");
    setEditingIcon(null);
    setIconScope("project");
    setIconNameTouched(false);
    setIconSearch("");
  };

  const handleSaveColor = () => {
    if (!newColorName.trim()) return;

    if (colorScope === "craftspace") {
      if (!craftspaceId) return;
      if (editingColor) {
        updateCraftspaceColorVariable(craftspaceId, editingColor.id, {
          name: newColorName,
          value: newColorValue,
        });
      } else {
        addCraftspaceColorVariable(craftspaceId, {
          name: newColorName,
          value: newColorValue,
        });
      }
    } else {
      if (!projectId || !craftspaceId) return;
      if (editingColor) {
        updateColorVariable(craftspaceId, projectId, editingColor.id, {
          name: newColorName,
          value: newColorValue,
        });
      } else {
        addColorVariable(craftspaceId, projectId, {
          name: newColorName,
          value: newColorValue,
        });
      }
    }

    resetColorForm();
    setColorDialogOpen(false);
  };

  const handleSaveSize = () => {
    if (!newSizeName.trim() || !newSizeValue.trim()) return;

    const value = `${newSizeValue}${newSizeUnit}`;

    if (sizeScope === "craftspace") {
      if (!craftspaceId) return;
      if (editingSize) {
        updateCraftspaceSizeVariable(craftspaceId, editingSize.id, {
          name: newSizeName,
          value: value,
        });
      } else {
        addCraftspaceSizeVariable(craftspaceId, {
          name: newSizeName,
          value: value,
        });
      }
    } else {
      if (!projectId || !craftspaceId) return;
      if (editingSize) {
        updateSizeVariable(craftspaceId, projectId, editingSize.id, {
          name: newSizeName,
          value: value,
        });
      } else {
        addSizeVariable(craftspaceId, projectId, {
          name: newSizeName,
          value: value,
        });
      }
    }

    resetSizeForm();
    setSizeDialogOpen(false);
  };

  const handleSaveFont = () => {
    if (!newFontName.trim() || !newFontValue.trim()) return;

    if (fontScope === "craftspace") {
      if (!craftspaceId) return;
      if (editingFont) {
        updateCraftspaceFontFamilyVariable(craftspaceId, editingFont.id, {
          name: newFontName,
          value: newFontValue,
        });
      } else {
        addCraftspaceFontFamilyVariable(craftspaceId, {
          name: newFontName,
          value: newFontValue,
        });
      }
    } else {
      if (!projectId || !craftspaceId) return;
      if (editingFont) {
        updateFontFamilyVariable(craftspaceId, projectId, editingFont.id, {
          name: newFontName,
          value: newFontValue,
        });
      } else {
        addFontFamilyVariable(craftspaceId, projectId, {
          name: newFontName,
          value: newFontValue,
        });
      }
    }

    resetFontForm();
    setFontDialogOpen(false);
  };

  const handleSaveIcon = () => {
    if (!newIconName.trim() || !newIconValue.trim()) return;

    if (iconScope === "craftspace") {
      if (!craftspaceId) return;
      if (editingIcon) {
        updateCraftspaceIconVariable(craftspaceId, editingIcon.id, {
          name: newIconName,
          value: newIconValue,
        });
      } else {
        addCraftspaceIconVariable(craftspaceId, {
          name: newIconName,
          value: newIconValue,
        });
      }
    } else {
      if (!projectId || !craftspaceId) return;
      if (editingIcon) {
        updateIconVariable(craftspaceId, projectId, editingIcon.id, {
          name: newIconName,
          value: newIconValue,
        });
      } else {
        addIconVariable(craftspaceId, projectId, {
          name: newIconName,
          value: newIconValue,
        });
      }
    }

    resetIconForm();
    setIconDialogOpen(false);
  };

  const handleEditColor = (color: ColorVariable, scope: "project" | "craftspace") => {
    setEditingColor(color);
    setNewColorName(color.name);
    setNewColorValue(color.value);
    setColorScope(scope);
    setColorDialogOpen(true);
  };

  const handleEditSize = (size: SizeVariable, scope: "project" | "craftspace") => {
    setEditingSize(size);
    setNewSizeName(size.name);
    setSizeScope(scope);

    // Extract number and unit from value
    const match = size.value.match(/^(\d+(?:\.\d+)?)(px|%|rem|em|vw|vh|deg)$/);
    if (match) {
      setNewSizeValue(match[1]);
      setNewSizeUnit(match[2]);
    } else {
      setNewSizeValue(size.value);
      setNewSizeUnit("px");
    }

    setSizeDialogOpen(true);
  };

  const handleEditFont = (font: FontFamilyVariable, scope: "project" | "craftspace") => {
    setEditingFont(font);
    setNewFontName(font.name);
    setNewFontValue(font.value);
    setFontScope(scope);
    setFontDialogOpen(true);
  };

  const handleEditIcon = (icon: IconVariable, scope: "project" | "craftspace") => {
    setEditingIcon(icon);
    setNewIconName(icon.name);
    setNewIconValue(icon.value);
    setIconScope(scope);
    setIconNameTouched(true);
    setIconDialogOpen(true);
  };

  const openColorDialogForScope = (scope: "project" | "craftspace") => {
    resetColorForm();
    setColorScope(scope);
    setColorDialogOpen(true);
  };

  const openSizeDialogForScope = (scope: "project" | "craftspace") => {
    resetSizeForm();
    setSizeScope(scope);
    setSizeDialogOpen(true);
  };

  const openFontDialogForScope = (scope: "project" | "craftspace") => {
    resetFontForm();
    setFontScope(scope);
    setFontDialogOpen(true);
  };

  const openIconDialogForScope = (scope: "project" | "craftspace") => {
    resetIconForm();
    setIconScope(scope);
    setIconDialogOpen(true);
  };

  if (!projectId) {
    return <div className="text-white">No project selected</div>;
  }

  const colorScopeLabel = colorScope === "craftspace" ? "Craftspace" : "Project";
  const sizeScopeLabel = sizeScope === "craftspace" ? "Craftspace" : "Project";
  const fontScopeLabel = fontScope === "craftspace" ? "Craftspace" : "Project";
  const iconScopeLabel = iconScope === "craftspace" ? "Craftspace" : "Project";

  const preferredIconOrder = [
    "Info",
    "ChevronUp",
    "ChevronDown",
    "ChevronLeft",
    "ChevronRight",
    "Plus",
    "Play",
    "CirclePlay",
    "Heart",
    "Star",
    "Volume1",
    "Volume2",
    "VolumeOff",
    "VolumeX",
    "ShoppingCart",
    "Map",
    "MapPin",
    "MoveUpRight",
    "SquareArrowOutUpRight",
  ];
  const lucideIconEntries = useMemo(() => {
    const entries = Object.entries(icons);
    const entryMap = new Map(entries);
    const preferredSet = new Set(preferredIconOrder);
    const orderedPreferred = preferredIconOrder
      .filter((name) => entryMap.has(name))
      .map((name) => [name, entryMap.get(name)] as [string, (typeof icons)[keyof typeof icons]]);
    const remaining = entries
      .filter(([name]) => !preferredSet.has(name))
      .sort(([a], [b]) => a.localeCompare(b));
    return [...orderedPreferred, ...remaining];
  }, []);

  const filteredLucideIcons = useMemo(() => {
    const query = iconSearch.trim().toLowerCase();
    if (!query) return lucideIconEntries;
    return lucideIconEntries.filter(([name]) => name.toLowerCase().includes(query));
  }, [iconSearch, lucideIconEntries]);

  const toKebabCase = (value: string) =>
    value
      .replace(/([a-z0-9])([A-Z])/g, "$1-$2")
      .replace(/[_\\s]+/g, "-")
      .toLowerCase();

  const renderIconPreview = (value: string) => {
    const trimmed = value.trim();
    if (trimmed.startsWith("lucide:")) {
      const iconName = trimmed.slice("lucide:".length);
      const IconComponent = (icons as Record<string, React.ComponentType<{ size?: number; className?: string }>>)[iconName];
      if (IconComponent) {
        return <IconComponent size={16} className="text-gray-200" />;
      }
    }
    const isUrl = /^(https?:)?\/\//i.test(trimmed);
    const isDataImage = /^data:image\//i.test(trimmed);
    if (!isUrl && !isDataImage) return null;
    return (
      <img
        src={trimmed}
        alt=""
        className="w-4 h-4 rounded bg-gray-700 object-contain"
      />
    );
  };

  return (
    <div className="h-full bg-gray-800 text-white overflow-auto">
      <div className="p-6 space-y-6">
        <div className="flex items-center space-x-3">
          <VariablesIcon size={20} className="text-white" />
          <h1 className="text-2xl font-bold text-white">Project Variables</h1>
        </div>

        <p className="text-sm text-gray-400">
          Project variables can be used across this specific project.
        </p>

        {/* Color Variables */}
        <Card className="bg-gray-800 border-gray-700">
          <CardHeader className="pb-3">
            <div className="flex items-center space-x-2">
              <Palette size={16} className="text-white" />
              <CardTitle className="text-sm text-white">Color</CardTitle>
            </div>
          </CardHeader>
          <Dialog
            open={colorDialogOpen}
            onOpenChange={(open) => {
              setColorDialogOpen(open);
              if (!open) resetColorForm();
            }}
          >
            <DialogContent className="bg-gray-800 border-gray-700">
              <DialogHeader>
                <DialogTitle className="text-white">
                  {editingColor
                    ? `Edit ${colorScopeLabel} Color Variable`
                    : `Add ${colorScopeLabel} Color Variable`}
                </DialogTitle>
              </DialogHeader>
              <div className="relative space-y-4">
                <div>
                  <Label className="text-gray-300">Variable Name</Label>
                  <Input
                    placeholder="e.g., primary-blue"
                    value={newColorName}
                    onChange={(e) => setNewColorName(e.target.value)}
                    className="bg-gray-700 border-gray-600 text-white"
                  />
                </div>
                <div>
                  <Label className="text-gray-300">Color</Label>
                  <div className="space-y-3">
                    <HexColorPicker color={newColorValue} onChange={setNewColorValue} />
                    <Input
                      value={newColorValue}
                      onChange={(e) => setNewColorValue(e.target.value)}
                      className="bg-gray-700 border-gray-600 text-white"
                    />
                  </div>
                </div>
                <div className="flex justify-end space-x-2">
                  <Button variant="outline" onClick={() => setColorDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button
                    onClick={handleSaveColor}
                    className="bg-gray-600 hover:bg-gray-700 text-white"
                  >
                    {editingColor ? "Update" : "Add"} Variable
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
          <CardContent className="space-y-3">
            <div className="space-y-2">
              <p className="text-[10px] font-semibold uppercase tracking-widest text-gray-400">Project</p>
              {projectColors.length > 0 &&
                projectColors.map((color) => (
                  <div
                    key={color.id}
                    className="flex items-center justify-between p-2 bg-gray-600 rounded"
                  >
                    <div className="flex items-center space-x-3">
                      <div
                        className="w-4 h-4 rounded"
                        style={{ backgroundColor: color.value }}
                      />
                      <span className="text-sm font-medium text-white">{color.name}</span>
                      <span className="text-xs text-gray-400">{color.value}</span>
                    </div>
                    <div className="flex items-center space-x-1">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleEditColor(color, "project")}
                        className="h-6 w-6 p-0 text-gray-400 hover:text-white"
                      >
                        <Edit2 size={12} />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => projectId && craftspaceId && deleteColorVariable(craftspaceId, projectId, color.id)}
                        className="h-6 w-6 p-0 text-gray-400 hover:text-red-400"
                      >
                        <Trash2 size={12} />
                      </Button>
                    </div>
                  </div>
                ))}
              <Button
                size="sm"
                variant="ghost"
                onClick={() => openColorDialogForScope("project")}
                className="h-7 w-7 p-0 text-gray-400 hover:text-white"
                title="Add project color variable"
                aria-label="Add project color variable"
              >
                <Plus size={14} />
              </Button>
            </div>
            {hasCraftspace && (
              <div className="space-y-2">
                <div className="h-px bg-gray-600/70" />
                <p className="text-[10px] font-semibold uppercase tracking-widest text-gray-400">Craftspace</p>
                {craftspaceColors.map((color) => (
                  <div key={color.id} className="flex items-center p-2 bg-gray-600 rounded">
                    <div className="flex items-center space-x-3">
                      <div className="w-4 h-4 rounded" style={{ backgroundColor: color.value }} />
                      <span className="text-sm font-medium text-white">{color.name}</span>
                      <span className="text-xs text-gray-400">{color.value}</span>
                    </div>
                  </div>
                ))}
                <Link
                  to="/dashboard?p=colors"
                  className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-300 transition-colors"
                >
                  <ExternalLink size={11} />
                  Open
                </Link>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Icon Variables */}
        <Card className="bg-gray-800 border-gray-700">
          <CardHeader className="pb-3">
            <div className="flex items-center space-x-2">
              <Image size={16} className="text-white" />
              <CardTitle className="text-sm text-white">Icon</CardTitle>
            </div>
          </CardHeader>
          <Dialog
            open={iconDialogOpen}
            onOpenChange={(open) => {
              setIconDialogOpen(open);
              if (!open) resetIconForm();
            }}
          >
            <DialogContent className="bg-gray-800 border-gray-700">
              <DialogHeader>
                <DialogTitle className="text-white">
                  {editingIcon
                    ? `Edit ${iconScopeLabel} Icon Variable`
                    : `Add ${iconScopeLabel} Icon Variable`}
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label className="text-gray-300">Variable Name</Label>
                  <Input
                    placeholder="e.g., play-icon"
                    value={newIconName}
                    onChange={(e) => {
                      setNewIconName(e.target.value);
                      setIconNameTouched(true);
                    }}
                    className="bg-gray-700 border-gray-600 text-white"
                  />
                </div>
                <div>
                  <Label className="text-gray-300">Icon URL or Data URI</Label>
                  <Input
                    placeholder="https://... or data:image/svg+xml;base64,..."
                    value={newIconValue}
                    onChange={(e) => setNewIconValue(e.target.value)}
                    className="bg-gray-700 border-gray-600 text-white"
                  />
                </div>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label className="text-gray-300">Lucide Icons</Label>
                    <span className="text-xs text-gray-400">
                      {filteredLucideIcons.length} icons
                    </span>
                  </div>
                  <Input
                    placeholder="Search icons..."
                    value={iconSearch}
                    onChange={(e) => setIconSearch(e.target.value)}
                    className="bg-gray-700 border-gray-600 text-white"
                  />
                  <div className="grid grid-cols-6 gap-2 max-h-44 overflow-y-auto rounded border border-gray-700 p-2 bg-gray-900/40">
                    {filteredLucideIcons.map(([name, IconComponent]) => (
                      <button
                        key={name}
                        type="button"
                        onClick={() => {
                          setNewIconValue(`lucide:${name}`);
                          if (!iconNameTouched) {
                            setNewIconName(toKebabCase(name));
                          }
                        }}
                        className="flex items-center justify-center h-9 w-9 rounded bg-gray-700 hover:bg-gray-600 text-gray-200"
                        title={name}
                        aria-label={`Use ${name} icon`}
                      >
                        <IconComponent size={16} />
                      </button>
                    ))}
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  {newIconValue.trim() ? (
                    <div className="h-9 w-9 flex items-center justify-center rounded bg-gray-700/40">
                      <div className="scale-150">{renderIconPreview(newIconValue)}</div>
                    </div>
                  ) : (
                    <div />
                  )}
                  <div className="flex justify-end space-x-2">
                  <Button variant="outline" onClick={() => setIconDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button
                    onClick={handleSaveIcon}
                    className="bg-gray-600 hover:bg-gray-700 text-white"
                  >
                    {editingIcon ? "Update" : "Add"} Variable
                  </Button>
                  </div>
                </div>
              </div>
            </DialogContent>
          </Dialog>
          <CardContent className="space-y-3">
            <div className="space-y-2">
              <p className="text-[10px] font-semibold uppercase tracking-widest text-gray-400">Project</p>
              {projectIcons.length > 0 &&
                projectIcons.map((icon) => (
                  <div
                    key={icon.id}
                    className="flex items-center justify-between p-2 bg-gray-600 rounded"
                  >
                    <div className="flex items-center space-x-3">
                      {renderIconPreview(icon.value)}
                      <span className="text-sm font-medium text-white">{icon.name}</span>
                      <span className="text-xs text-gray-400 truncate max-w-[180px]">
                        {icon.value}
                      </span>
                    </div>
                    <div className="flex items-center space-x-1">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleEditIcon(icon, "project")}
                        className="h-6 w-6 p-0 text-gray-400 hover:text-white"
                      >
                        <Edit2 size={12} />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => projectId && craftspaceId && deleteIconVariable(craftspaceId, projectId, icon.id)}
                        className="h-6 w-6 p-0 text-gray-400 hover:text-red-400"
                      >
                        <Trash2 size={12} />
                      </Button>
                    </div>
                  </div>
                ))}
              <Button
                size="sm"
                variant="ghost"
                onClick={() => openIconDialogForScope("project")}
                className="h-7 w-7 p-0 text-gray-400 hover:text-white"
                title="Add project icon variable"
                aria-label="Add project icon variable"
              >
                <Plus size={14} />
              </Button>
            </div>
            {hasCraftspace && (
              <div className="space-y-2">
                <div className="h-px bg-gray-600/70" />
                <p className="text-[10px] font-semibold uppercase tracking-widest text-gray-400">Craftspace</p>
                {craftspaceIcons.map((icon) => (
                  <div key={icon.id} className="flex items-center p-2 bg-gray-600 rounded">
                    <div className="flex items-center space-x-3">
                      {renderIconPreview(icon.value)}
                      <span className="text-sm font-medium text-white">{icon.name}</span>
                      <span className="text-xs text-gray-400 truncate max-w-[180px]">{icon.value}</span>
                    </div>
                  </div>
                ))}
                <Link
                  to="/dashboard?p=icons"
                  className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-300 transition-colors"
                >
                  <ExternalLink size={11} />
                  Open
                </Link>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Size Variables */}
        <Card className="bg-gray-800 border-gray-700">
          <CardHeader className="pb-3">
            <div className="flex items-center space-x-2">
              <Ruler size={16} className="text-white" />
              <CardTitle className="text-sm text-white">Size</CardTitle>
            </div>
          </CardHeader>
          <Dialog
            open={sizeDialogOpen}
            onOpenChange={(open) => {
              setSizeDialogOpen(open);
              if (!open) resetSizeForm();
            }}
          >
            <DialogContent className="bg-gray-800 border-gray-700">
              <DialogHeader>
                <DialogTitle className="text-white">
                  {editingSize
                    ? `Edit ${sizeScopeLabel} Size Variable`
                    : `Add ${sizeScopeLabel} Size Variable`}
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label className="text-gray-300">Variable Name</Label>
                  <Input
                    placeholder="e.g., border-radius-main"
                    value={newSizeName}
                    onChange={(e) => setNewSizeName(e.target.value)}
                    className="bg-gray-700 border-gray-600 text-white"
                  />
                </div>
                <div>
                  <Label className="text-gray-300">Size</Label>
                  <div className="flex space-x-2">
                    <Input
                      type="number"
                      placeholder="20"
                      value={newSizeValue}
                      onChange={(e) => setNewSizeValue(e.target.value)}
                      className="bg-gray-700 border-gray-600 text-white flex-1"
                    />
                    <Select value={newSizeUnit} onValueChange={setNewSizeUnit}>
                      <SelectTrigger className="w-20 bg-gray-700 border-gray-600 text-white">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-gray-700 border-gray-600">
                        <SelectItem value="px" className="text-white">
                          px
                        </SelectItem>
                        <SelectItem value="%" className="text-white">
                          %
                        </SelectItem>
                        <SelectItem value="rem" className="text-white">
                          rem
                        </SelectItem>
                        <SelectItem value="em" className="text-white">
                          em
                        </SelectItem>
                        <SelectItem value="vw" className="text-white">
                          vw
                        </SelectItem>
                        <SelectItem value="vh" className="text-white">
                          vh
                        </SelectItem>
                        <SelectItem value="deg" className="text-white">
                          deg
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="flex justify-end space-x-2">
                  <Button variant="outline" onClick={() => setSizeDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button
                    onClick={handleSaveSize}
                    className="bg-gray-600 hover:bg-gray-700 text-white"
                  >
                    {editingSize ? "Update" : "Add"} Variable
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
          <CardContent className="space-y-3">
            <div className="space-y-2">
              <p className="text-[10px] font-semibold uppercase tracking-widest text-gray-400">Project</p>
              {projectSizes.length > 0 &&
                projectSizes.map((size) => (
                  <div
                    key={size.id}
                    className="flex items-center justify-between p-2 bg-gray-600 rounded"
                  >
                    <div className="flex items-center space-x-3">
                      <span className="text-sm font-medium text-white">{size.name}</span>
                      <span className="text-xs text-gray-400">{size.value}</span>
                    </div>
                    <div className="flex items-center space-x-1">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleEditSize(size, "project")}
                        className="h-6 w-6 p-0 text-gray-400 hover:text-white"
                      >
                        <Edit2 size={12} />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => projectId && craftspaceId && deleteSizeVariable(craftspaceId, projectId, size.id)}
                        className="h-6 w-6 p-0 text-gray-400 hover:text-red-400"
                      >
                        <Trash2 size={12} />
                      </Button>
                    </div>
                  </div>
                ))}
              <Button
                size="sm"
                variant="ghost"
                onClick={() => openSizeDialogForScope("project")}
                className="h-7 w-7 p-0 text-gray-400 hover:text-white"
                title="Add project size variable"
                aria-label="Add project size variable"
              >
                <Plus size={14} />
              </Button>
            </div>
            {hasCraftspace && (
              <div className="space-y-2">
                <div className="h-px bg-gray-600/70" />
                <p className="text-[10px] font-semibold uppercase tracking-widest text-gray-400">Craftspace</p>
                {craftspaceSizes.map((size) => (
                  <div key={size.id} className="flex items-center p-2 bg-gray-600 rounded">
                    <div className="flex items-center space-x-3">
                      <span className="text-sm font-medium text-white">{size.name}</span>
                      <span className="text-xs text-gray-400">{size.value}</span>
                    </div>
                  </div>
                ))}
                <Link
                  to="/dashboard?p=sizes"
                  className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-300 transition-colors"
                >
                  <ExternalLink size={11} />
                  Open
                </Link>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Font Family Variables */}
        <Card className="bg-gray-800 border-gray-700">
          <CardHeader className="pb-3">
            <div className="flex items-center space-x-2">
              <Type size={16} className="text-white" />
              <CardTitle className="text-sm text-white">Font Family</CardTitle>
            </div>
          </CardHeader>
          <Dialog
            open={fontDialogOpen}
            onOpenChange={(open) => {
              setFontDialogOpen(open);
              if (!open) resetFontForm();
            }}
          >
            <DialogContent className="bg-gray-800 border-gray-700">
              <DialogHeader>
                <DialogTitle className="text-white">
                  {editingFont
                    ? `Edit ${fontScopeLabel} Font Variable`
                    : `Add ${fontScopeLabel} Font Variable`}
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label className="text-gray-300">Variable Name</Label>
                  <Input
                    placeholder="e.g., heading-font"
                    value={newFontName}
                    onChange={(e) => setNewFontName(e.target.value)}
                    className="bg-gray-700 border-gray-600 text-white"
                  />
                </div>
                <div>
                  <Label className="text-gray-300">Font Family</Label>
                  <Input
                    placeholder="e.g., Inter, Arial, sans-serif"
                    value={newFontValue}
                    onChange={(e) => setNewFontValue(e.target.value)}
                    className="bg-gray-700 border-gray-600 text-white"
                  />
                </div>
                <div className="flex justify-end space-x-2">
                  <Button variant="outline" onClick={() => setFontDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button
                    onClick={handleSaveFont}
                    className="bg-gray-600 hover:bg-gray-700 text-white"
                  >
                    {editingFont ? "Update" : "Add"} Variable
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
          <CardContent className="space-y-3">
            <div className="space-y-2">
              <p className="text-[10px] font-semibold uppercase tracking-widest text-gray-400">Project</p>
              {projectFonts.length > 0 &&
                projectFonts.map((font) => (
                  <div
                    key={font.id}
                    className="flex items-center justify-between p-2 bg-gray-600 rounded"
                  >
                    <div className="flex items-center space-x-3">
                      <span className="text-sm font-medium text-white">{font.name}</span>
                      <span className="text-xs text-gray-400" style={{ fontFamily: font.value }}>
                        {font.value}
                      </span>
                    </div>
                    <div className="flex items-center space-x-1">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleEditFont(font, "project")}
                        className="h-6 w-6 p-0 text-gray-400 hover:text-white"
                      >
                        <Edit2 size={12} />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => projectId && craftspaceId && deleteFontFamilyVariable(craftspaceId, projectId, font.id)}
                        className="h-6 w-6 p-0 text-gray-400 hover:text-red-400"
                      >
                        <Trash2 size={12} />
                      </Button>
                    </div>
                  </div>
                ))}
              <Button
                size="sm"
                variant="ghost"
                onClick={() => openFontDialogForScope("project")}
                className="h-7 w-7 p-0 text-gray-400 hover:text-white"
                title="Add project font variable"
                aria-label="Add project font variable"
              >
                <Plus size={14} />
              </Button>
            </div>
            {hasCraftspace && (
              <div className="space-y-2">
                <div className="h-px bg-gray-600/70" />
                <p className="text-[10px] font-semibold uppercase tracking-widest text-gray-400">Craftspace</p>
                {craftspaceFonts.map((font) => (
                  <div key={font.id} className="flex items-center p-2 bg-gray-600 rounded">
                    <div className="flex items-center space-x-3">
                      <span className="text-sm font-medium text-white">{font.name}</span>
                      <span className="text-xs text-gray-400" style={{ fontFamily: font.value }}>{font.value}</span>
                    </div>
                  </div>
                ))}
                <Link
                  to="/dashboard?p=font"
                  className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-300 transition-colors"
                >
                  <ExternalLink size={11} />
                  Open
                </Link>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Variables;
